// Compact pricing teaser for the Lead Gen Tools landing page.
//
// Replaces the two full pricing sections that used to sit on the homepage
// (the AgencyPricingPlans cards + the "Talk to us" PricingTease). The full
// detail now lives on the dedicated /pricing page; the homepage keeps just
// this short band so warm funnel visitors still see pricing exists and have
// a one-click path to the plans.
//
// Keeps id="pricing" because two in-page CTAs on the landing
// (Hero + FinalCTA) smooth-scroll to getElementById('pricing'), and the
// stripped funnel header / tool pages deep-link to /lead-gen-tools#pricing.

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

const HIGHLIGHTS = [
  'Free to start',
  'Paid plans from $9/mo',
  'Pay only for credits you use',
  'Cancel anytime',
];

export function PricingTeaserBand() {
  return (
    <section
      id="pricing"
      className="relative scroll-mt-28 bg-gradient-to-b from-[#0d0d14] to-gray-950 py-28 lg:py-32"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(139, 92, 246, 0.15), transparent 50%)',
        }}
      />
      <div className="relative container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl text-center"
        >
          <div className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-400">
            Pricing
          </div>
          <h2
            className="font-bold tracking-tight text-white"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', lineHeight: 1.1 }}
          >
            Pricing that scales with your lead flow.
          </h2>
          <p className="mt-5 text-lg text-white/60">
            Start free, upgrade when the leads start landing. Three plans:
            Starter, Growth, and Scale.
          </p>

          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-2 text-sm text-white/80">
                <Check className="size-4 shrink-0 text-emerald-400" />
                <span>{h}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition-transform hover:scale-105"
            >
              See all plans and pricing
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
